import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useCustomer } from "@/context/CustomerContext";
import StoreShell from "@/components/StoreShell";
import { PageTitle } from "../Shop";
import { Splash, ErrorView } from "../Storefront";
import { normMobile } from "@/lib/personValidation";
import { Button, Card, Field, inputCls, inputStyle } from "./AccountUI";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Log in / create account / forgot password, all on one themed page.
export default function AccountAuth() {
  const s = useStore();
  const cu = useCustomer();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("login");        // login | signup | verify | forgot | reset
  const [f, setF] = useState({ name: "", mobile: "", email: "", password: "", identifier: "", code: "" });
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [devCode, setDevCode] = useState("");
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErrs((e) => ({ ...e, [k]: undefined })); };

  if (s.loading) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;
  const dest = loc.state?.from || `/store/${s.slug}/account/orders`;
  if (cu.customer) return <Navigate to={dest} replace />;

  const go = (m) => { setMode(m); setErrs({}); };
  const call = async (fn) => {
    setBusy(true);
    try { return await fn(); }
    catch (err) { const msg = apiErr(err.response?.data?.detail); toast.error(msg); setErrs({ form: msg }); return null; }
    finally { setBusy(false); }
  };
  const pwErr = (v) => (v.length < 8 ? "Use at least 8 characters." : !/[A-Za-z]/.test(v) || !/\d/.test(v) ? "Include a letter and a number." : "");

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (mode === "login") {
      if (!f.identifier.trim()) er.identifier = "Enter your email or mobile number.";
      if (!f.password) er.password = "Enter your password.";
      setErrs(er); if (Object.keys(er).length) return;
      const r = await call(() => cu.api.post("/login", { identifier: f.identifier, password: f.password }));
      if (r) { cu.signIn(r.data); nav(dest, { replace: true }); }
    } else if (mode === "signup") {
      if (f.name.trim().length < 2 || !/\p{L}/u.test(f.name)) er.name = "Enter your name.";
      if (!/^[6-9]\d{9}$/.test(normMobile(f.mobile))) er.mobile = "Enter a valid 10-digit mobile number.";
      if (!EMAIL.test(f.email.trim())) er.email = "Enter a valid email address.";
      if (pwErr(f.password)) er.password = pwErr(f.password);
      setErrs(er); if (Object.keys(er).length) return;
      const r = await call(() => cu.api.post("/signup", { name: f.name, mobile: f.mobile, email: f.email, password: f.password }));
      if (r) { setSentTo(r.data.masked); setDevCode(r.data.dev_code || ""); go("verify"); }
    } else if (mode === "verify") {
      if (!/^\d{6}$/.test(f.code.trim())) { setErrs({ code: "Enter the 6-digit code." }); return; }
      const r = await call(() => cu.api.post("/verify", { email: f.email, code: f.code }));
      if (r) { cu.signIn(r.data); toast.success("Your account is ready."); nav(dest, { replace: true }); }
    } else if (mode === "forgot") {
      if (!EMAIL.test(f.email.trim())) { setErrs({ email: "Enter a valid email address." }); return; }
      const r = await call(() => cu.api.post("/forgot", { email: f.email }));
      if (r) { setSentTo(r.data.masked); setDevCode(r.data.dev_code || ""); go("reset"); }
    } else if (mode === "reset") {
      if (!/^\d{6}$/.test(f.code.trim())) er.code = "Enter the 6-digit code.";
      if (pwErr(f.password)) er.password = pwErr(f.password);
      setErrs(er); if (Object.keys(er).length) return;
      const r = await call(() => cu.api.post("/reset", { email: f.email, code: f.code, password: f.password }));
      if (r) { cu.signIn(r.data); toast.success("Password updated."); nav(dest, { replace: true }); }
    }
  };

  const input = (k, props = {}) => <input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`acct-${k}`} className={inputCls} style={inputStyle(s, errs[k])} {...props} />;
  const titles = { login: ["Log in", "Access your orders, addresses and profile."], signup: ["Create your account", "Track orders and check out faster."], verify: ["Check your email", ""], forgot: ["Forgot password", "We'll email you a code to reset it."], reset: ["Reset password", ""] };
  const btn = { login: "Log in", signup: "Create account", verify: "Verify and continue", forgot: "Send code", reset: "Set new password" }[mode];

  return (
    <StoreShell>
      <PageTitle s={s} title={titles[mode][0]} subtitle={titles[mode][1]} />
      <div className="max-w-md mx-auto px-5 py-10">
        <Card s={s} className="p-6">
          <form onSubmit={submit} className="space-y-4" noValidate data-testid={`acct-form-${mode}`}>
            {(mode === "verify" || mode === "reset") && (
              <p className="text-sm" style={{ color: s.muted }}>We sent a 6-digit code to <b style={{ color: s.c.text }}>{sentTo}</b>. It expires in 15 minutes.
                {devCode && <span className="block mt-1 text-xs">(Development mode code: <b data-testid="acct-dev-code">{devCode}</b>)</span>}</p>
            )}
            {mode === "login" && <Field s={s} label="Email or mobile" error={errs.identifier}>{input("identifier", { autoComplete: "username" })}</Field>}
            {mode === "signup" && <>
              <Field s={s} label="Full name" error={errs.name}>{input("name", { autoComplete: "name", maxLength: 80 })}</Field>
              <Field s={s} label="Mobile" error={errs.mobile}>{input("mobile", { autoComplete: "tel", inputMode: "numeric", maxLength: 16 })}</Field>
            </>}
            {(mode === "signup" || mode === "forgot") && <Field s={s} label="Email" error={errs.email} hint={mode === "signup" ? "We'll send a code to confirm it." : undefined}>{input("email", { type: "email", autoComplete: "email" })}</Field>}
            {(mode === "verify" || mode === "reset") && <Field s={s} label="6-digit code" error={errs.code}>{input("code", { inputMode: "numeric", maxLength: 6, autoComplete: "one-time-code" })}</Field>}
            {(mode === "login" || mode === "signup" || mode === "reset") && (
              <Field s={s} label={mode === "reset" ? "New password" : "Password"} error={errs.password} hint={mode !== "login" ? "At least 8 characters, with a letter and a number." : undefined}>
                {input("password", { type: "password", autoComplete: mode === "login" ? "current-password" : "new-password", maxLength: 72 })}
              </Field>
            )}
            {errs.form && <p role="alert" className="text-sm text-red-600" data-testid="acct-form-error">{errs.form}</p>}
            <Button s={s} type="submit" disabled={busy} className="w-full !py-3" data-testid="acct-submit">{busy ? "Please wait…" : btn}</Button>
          </form>
          <div className="mt-5 text-sm text-center space-y-1.5" style={{ color: s.muted }}>
            {mode === "login" && <>
              <button type="button" onClick={() => go("forgot")} className="underline" data-testid="acct-forgot-link">Forgot password?</button>
              <div>New here? <button type="button" onClick={() => go("signup")} className="font-semibold underline" style={{ color: s.c.primary }} data-testid="acct-signup-link">Create an account</button></div>
            </>}
            {mode !== "login" && <div>{mode === "signup" ? "Already have an account? " : ""}<button type="button" onClick={() => go("login")} className="font-semibold underline" style={{ color: s.c.primary }} data-testid="acct-login-link">Back to log in</button></div>}
            <div><Link to={`/store/${s.slug}/track`} className="underline">Track an order without an account</Link></div>
          </div>
        </Card>
      </div>
    </StoreShell>
  );
}
